<template>
  <div class="mention-input-wrapper">
    <div
      class="editor scrollable"
      ref="editor"
      contenteditable
      @input="onInput"
      @keydown="onKeydown"
      @click="showSuggestions = false"
    ></div>

    <ul
      v-if="showSuggestions"
      class="suggestions scrollable"
      :style="suggestionPosition"
    >
      <li
        v-for="(user, index) in users"
        :key="user.id"
        :class="{ selected: index === selectedIndex }"
        @mousedown.prevent="selectUser(user)"
        ref="userItems"
      >
        {{ user.name }}
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import type { UserData } from "@/store/types"

export default defineComponent({
  name: "MentionInput",
  props: {
    asyncSearch: {
      type: Function as PropType<(query: string) => Promise<UserData[]>>,
      required: true,
    },
    modelValue: {
      type: String,
      default: "",
    },
    inputStyles: {
      type: [String, Object],
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      editor: null as HTMLDivElement | null,
      showSuggestions: false,
      selectedIndex: 0,
      searchText: "",
      mentionTrigger: null as string | null,
      users: [] as UserData[],
      searchTimeout: undefined as number | undefined,
      suggestionPosition: {
        left: "30px",
      },
      text: this.modelValue,
    }
  },

  mounted() {
    if (this.$refs.editor) {
      ;(this.$refs.editor as HTMLDivElement).innerText = this.modelValue
    }
  },

  watch: {
    modelValue(newVal: string) {
      const editor = this.$refs.editor as HTMLDivElement
      if (!editor) return

      const plainText = this.getPlainText()
      if (plainText === newVal) return

      const sel = window.getSelection()
      let currentRange: Range | null = null
      if (sel && sel.rangeCount) {
        currentRange = sel.getRangeAt(0).cloneRange()
      }

      editor.innerText = newVal

      if (sel && currentRange) {
        sel.removeAllRanges()
        sel.addRange(currentRange)
      }
    },
  },

  methods: {
    getPlainText(): string {
      return (this.$refs.editor as HTMLElement).innerHTML
        .replace(/<span[^>]*>(.*?)<\/span>/g, "$1")
        .replace(/<div>/g, "\n")
        .replace(/<\/div>/g, "")
        .replace(/<br>/g, "\n")
        .replace(/&nbsp;/g, " ")
        .replace(/\u00a0/g, " ")
        .replace(/ +$/g, "")
    },

    async onInput() {
      const sel = window.getSelection()
      if (!sel || !sel.anchorNode) return

      const textUpToCaret = (sel.anchorNode.textContent ?? "").slice(
        0,
        sel.anchorOffset
      )

      const mentionMatch = textUpToCaret.match(/@[\w.-]*$/)
      const lastWord = mentionMatch ? mentionMatch[0] : null

      if (lastWord?.startsWith("@")) {
        this.searchText = lastWord.slice(1)
        this.mentionTrigger = lastWord

        if (this.searchText) {
          clearTimeout(this.searchTimeout)
          this.searchTimeout = window.setTimeout(async () => {
            try {
              this.users = await this.asyncSearch(this.searchText)
              this.showSuggestions = true

              this.$nextTick(() => {
                const selection = window.getSelection()
                if (!selection?.rangeCount) return

                const range = selection.getRangeAt(0)
                const rect =
                  range.getClientRects()[0] || range.getBoundingClientRect()
                if (!rect) return

                const container = this.$refs.editor as HTMLElement
                const containerRect = container.getBoundingClientRect()

                const left =
                  rect.left - containerRect.left + container.scrollLeft

                this.suggestionPosition = {
                  left: `${left}px`,
                }
              })
            } catch (err) {
              console.error("Failed to fetch mentions:", err)
              this.users = []
            }
          }, 300)
        }
      } else {
        this.showSuggestions = false
      }

      this.$emit("update:modelValue", this.getPlainText())
    },
    scrollHighlightedIntoView() {
      const items = this.$refs.userItems as HTMLElement[]
      const highlighted = items[this.selectedIndex]

      if (highlighted) {
        const container = highlighted.parentElement
        if (!container) return

        const containerTop = container.scrollTop
        const containerBottom = containerTop + container.clientHeight

        const elementTop = highlighted.offsetTop
        const elementBottom = elementTop + highlighted.offsetHeight

        if (elementBottom > containerBottom) {
          container.scrollTop += elementBottom - containerBottom
        } else if (elementTop < containerTop) {
          container.scrollTop -= containerTop - elementTop
        }
      }
    },

    onKeydown(e: KeyboardEvent) {
      if (e.key === "Enter" && e.shiftKey) {
        return
      }

      if (e.key === "Enter" && !this.showSuggestions) {
        e.preventDefault()
        if (this.modelValue.trim()) {
          this.$emit("submit")
        }
        return
      }

      if (!this.showSuggestions) return

      if (e.key === "ArrowDown") {
        e.preventDefault()
        this.selectedIndex = (this.selectedIndex + 1) % this.users.length
        this.scrollHighlightedIntoView()
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        this.selectedIndex =
          (this.selectedIndex - 1 + this.users.length) % this.users.length
        this.scrollHighlightedIntoView()
      } else if (e.key === "Enter") {
        e.preventDefault()
        const user = this.users[this.selectedIndex] || this.users[0]
        if (user) this.selectUser(user)
      } else if (e.key === "Escape") {
        this.showSuggestions = false
      }
    },

    selectUser(user: UserData) {
      const sel = window.getSelection()
      if (!sel || !sel.rangeCount) return

      const node = sel.anchorNode

      const text = node?.textContent ?? ""
      const caretPos = sel.anchorOffset
      const textUpToCaret = text.slice(0, caretPos)
      const mentionMatch = textUpToCaret.match(/@[\w.-]*$/)
      if (!mentionMatch) return

      const mentionStart = caretPos - mentionMatch[0].length

      const mentionRange = document.createRange()

      if (node) {
        mentionRange.setStart(node, mentionStart)
        mentionRange.setEnd(node, caretPos)
      }

      sel.removeAllRanges()
      sel.addRange(mentionRange)

      const spanHTML = `<span class="mention" contenteditable="false" data-userid="${user.id}">@${user.name}</span>&nbsp;`
      document.execCommand("insertHTML", false, spanHTML)

      this.showSuggestions = false
      this.selectedIndex = 0
      this.users = []

      this.$emit("update:modelValue", this.getPlainText())
    },
    getMentions(): { id: string; name: string }[] {
      const mentions: { id: string; name: string }[] = []
      if (!this.$refs.editor) return mentions

      const spans = (this.$refs.editor as HTMLElement).querySelectorAll(
        "span.mention"
      )
      spans.forEach((el) => {
        const id = el.getAttribute("data-userid")
        const name = el.textContent?.replace("@", "")
        if (id && name) mentions.push({ id, name })
      })

      return mentions
    },
  },
})
</script>

<style lang="scss" scoped>
.mention-input-wrapper {
  position: relative;
  width: calc(100% - 35px);
}

.editor {
  font-family: "Montserrat";
  font-style: normal;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  width: 100%;
  min-height: 20px;
  border-radius: 6px;
  line-height: 1.5;
  max-height: 150px;

  &:focus-visible {
    outline: none;
  }
}

.mention {
  background-color: #d1ecf1;
  color: #0c5460;
  padding: 2px 5px;
  border-radius: 4px;
}

.suggestions {
  position: absolute;
  background-color: #212429;
  list-style: none;
  padding: 0;
  margin: 0;
  z-index: 10;
  bottom: 20px;
  max-height: 150px;
  scrollbar-width: thin;
  scrollbar-color: #222223 #373a40;
}

.suggestions li {
  padding: 8px 12px;
  cursor: pointer;
  color: #ffffff;
  font-size: 14px;
}

.suggestions li.selected {
  background-color: #3c424b;
  color: white;
}
</style>
